import { DatePipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { checkClientsFreemium } from '@lib/members/data-access';
import { MemberProfilesService } from '@lib/members/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';

import { DEFAULT_PAGE_SIZE, EMPTY_RESPONSE } from '@lib/commons/constants/request.const';
import { calculateAge } from '@lib/commons/util-functions/calculate-age.function';
import { DeepEqual } from '@lib/commons/util-functions/deep-equal.function';
import { formatDate } from '@lib/commons/util-functions/format-date.function';
import { findActiveContract } from '@lib/commons/util-functions/handle-contract.function';

import { MemberProfile } from '@lib/core/models/member.interface';
import { PagedResponse } from '@lib/core/models/paged-response';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

interface ClientsFiltersControls {
  search: FormControl<string>;
  status: FormControl<ClientStatusFilters>;
}

interface ClientsFilters {
  search: string;
  status: ClientStatusFilters;
}

enum ClientStatusFilters {
  All = 'All',
  Active = 'Active',
}

enum ClientsListColumnsHeaders {
  Name = 'fullName',
  Phone = 'phoneNumber',
  BirthDate = 'birthDate',
  PolicyNumber = 'policyNumber',
  PolicyTerm = 'policyStartDate',
  Doctor = 'doctor',
  Actions = 'actions',
}

const ClientsListColumns: string[] = [
  ClientsListColumnsHeaders.Name,
  ClientsListColumnsHeaders.Phone,
  ClientsListColumnsHeaders.BirthDate,
  ClientsListColumnsHeaders.PolicyNumber,
  ClientsListColumnsHeaders.PolicyTerm,
  ClientsListColumnsHeaders.Doctor,
];

@Component({
  selector: 'members-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgOptimizedImage,
    MatDivider,
    InfiniteScrollDirective,
    SpinLoadingComponent,
    LoaderComponent,
    RouterLink,
    MatPrefix,
    MatInput,
    MatSuffix,
    MatIconButton,
    MatFormField,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCellDef,
    MatCell,
    DatePipe,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatLabel,
    MatRow,
    TranslatePipe,
    MatFormField,
    MatPrefix,
    MatInput,
  ],
  styles: `
    :host {
      @apply h-full;
    }
  `,
  templateUrl: './members-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembersListComponent implements OnInit {
  private memberProfilesService = inject(MemberProfilesService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  protected readonly clientStatusFilters = ClientStatusFilters;
  protected readonly headers = ClientsListColumnsHeaders;
  protected readonly columns = ClientsListColumns;
  protected readonly checkFreemium = checkClientsFreemium;
  protected readonly calculateAge = calculateAge;
  protected readonly formatDate = formatDate;
  protected readonly findActiveContract = findActiveContract;

  protected clientsFilterForm!: FormGroup<ClientsFiltersControls>;
  protected nextFetchedPage: number = 1;

  protected readonly loading = signal<boolean>(true);
  protected readonly profilesLoading = signal<boolean>(false);
  protected readonly clients = signal<MemberProfile[]>([]);
  protected readonly hasNextPage = signal<boolean>(true);

  protected readonly filters$ = new BehaviorSubject<ClientsFilters>({
    search: '',
    status: ClientStatusFilters.Active,
  });

  protected get filtersFc() {
    return this.clientsFilterForm.controls;
  }

  ngOnInit() {
    this.initFiltersForm();
    this.initFiltersWatcher();
    this.initClientsFetcher();
  }

  protected initFiltersForm() {
    this.clientsFilterForm = new FormGroup<ClientsFiltersControls>({
      search: new FormControl('', { nonNullable: true }),
      status: new FormControl(ClientStatusFilters.Active, { nonNullable: true }),
    });
  }

  private initFiltersWatcher() {
    this.clientsFilterForm.valueChanges
      .pipe(
        debounceTime(300),
        map(form => ({
          search: form.search.trim().length >= 3 ? form.search.trim() : '',
          status: form.status || ClientStatusFilters.Active,
        })),
        distinctUntilChanged((prev, curr) => DeepEqual<ClientsFilters>(prev, curr)),
        tap(() => {
          this.loading.set(true);
          this.hasNextPage.set(true);
          this.clients.set([]);
          this.nextFetchedPage = 1;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(filters => this.filters$.next(filters));
  }

  protected fetchNextPage() {
    if (this.hasNextPage()) {
      this.filters$.next(<ClientsFilters>this.clientsFilterForm.value);
    }
  }

  private initClientsFetcher() {
    this.filters$
      .pipe(
        filter(() => this.hasNextPage()),
        tap(() => this.profilesLoading.set(true)),
        switchMap(filters =>
          this.fetchClients(filters).pipe(
            catchError(() => {
              this.snackBar.open('Не вдалось підвантажити профілі клієнтів', 'OK', { duration: 10000 });
              return of(<PagedResponse<MemberProfile>>EMPTY_RESPONSE);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          const data: MemberProfile[] = result.value;
          this.hasNextPage.set(result.hasNextPage);
          this.clients.update(clients => [...clients, ...data]);
          this.nextFetchedPage++;
          this.profilesLoading.set(false);
          this.loading.set(false);
        },
        error: () => {
          this.profilesLoading.set(false);
          this.loading.set(false);
        },
      });
  }

  private fetchClients(filters: ClientsFilters) {
    const statusFilter: ClientStatusFilters = filters.status;
    const payload = {
      page: this.nextFetchedPage,
      pageSize: DEFAULT_PAGE_SIZE,
      filters: {
        isActive: statusFilter && statusFilter !== ClientStatusFilters.All ? true : null,
      },
    };

    if (filters.search.trim().length >= 3)
      return forkJoin([
        this.memberProfilesService.searchProfiles({
          ...payload,
          filters: { ...payload.filters, fullName: `${filters.search.trim()}` },
        }),
        this.memberProfilesService.searchProfiles({
          ...payload,
          filters: { ...payload.filters, phoneNumber: `${filters.search.trim()}` },
        }),
      ]).pipe(map(([byName, byPhone]) => (byName.value.length > 0 ? byName : byPhone)));
    else return this.memberProfilesService.searchProfiles(payload);
  }

  protected clearSearch() {
    this.filtersFc.search.setValue('');
  }

  protected setStatusFilterValue(status: ClientStatusFilters) {
    this.filtersFc.status.setValue(status);
  }
}
